import { useEffect, useRef, useState, type PointerEvent } from 'react'
import type { ScratchArea, ScratchImage } from '@/lib/purchase-scratch'
import { markScratchCoverage } from './scratch-area'
import styles from './PurchaseScratchCard.module.css'

export function ImageScratchCard({
  cover,
  result,
  area,
  revealed,
  onReveal,
  imageOnly = false,
}: {
  cover: ScratchImage
  result: ScratchImage
  area: ScratchArea
  revealed: boolean
  onReveal: () => void
  imageOnly?: boolean
}) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const pointer = useRef<number | null>(null)
  const previous = useRef<{ x: number; y: number } | null>(null)
  const cells = useRef(new Set<number>())
  const done = useRef(revealed)
  const [progress, setProgress] = useState(0)
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)
  const width = Math.max(1, Math.round((1000 * area.width) / 100))
  const height = Math.max(
    1,
    Math.round((((1000 * cover.height) / cover.width) * area.height) / 100)
  )
  useEffect(() => {
    if (revealed) return
    let active = true
    const image = new Image()
    image.onload = () => {
      if (!active) return
      const context = canvas.current?.getContext('2d')
      if (!context) {
        setFailed(true)
        return
      }
      if (
        area.width <= 0 ||
        area.height <= 0 ||
        area.x < 0 ||
        area.y < 0 ||
        area.x + area.width > 100.00001 ||
        area.y + area.height > 100.00001
      ) {
        setFailed(true)
        return
      }
      context.globalCompositeOperation = 'source-over'
      context.fillStyle = '#e6e8e1'
      context.fillRect(0, 0, width, height)
      context.drawImage(
        image,
        (image.width * area.x) / 100,
        (image.height * area.y) / 100,
        (image.width * area.width) / 100,
        (image.height * area.height) / 100,
        0,
        0,
        width,
        height
      )
      setReady(true)
    }
    image.onerror = () => {
      if (active) setFailed(true)
    }
    // No pixel readback or export: works with existing storage/CDN images without canvas CORS access.
    image.src = cover.url
    return () => {
      active = false
      image.onload = null
      image.onerror = null
    }
  }, [area, cover.url, height, revealed, width])
  function finish() {
    if (done.current) return
    done.current = true
    onReveal()
  }
  function scratch(event: PointerEvent<HTMLCanvasElement>) {
    if (!ready || done.current || revealed) return
    const rect = event.currentTarget.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    const point = {
      x: ((event.clientX - rect.left) / rect.width) * width,
      y: ((event.clientY - rect.top) / rect.height) * height,
    }
    const from = previous.current ?? point
    const context = event.currentTarget.getContext('2d')
    if (!context) return
    const radius = Math.max(4, Math.min(width, height) * 0.11)
    context.globalCompositeOperation = 'destination-out'
    context.lineWidth = radius * 2
    context.lineCap = 'round'
    context.beginPath()
    context.moveTo(from.x, from.y)
    context.lineTo(point.x, point.y)
    context.stroke()
    context.beginPath()
    context.arc(point.x, point.y, radius, 0, Math.PI * 2)
    context.fill()
    previous.current = point
    const coverage = markScratchCoverage(
      cells.current,
      from,
      point,
      width,
      height,
      radius
    )
    setProgress(Math.min(100, Math.round((coverage / 0.55) * 100)))
    if (coverage >= 0.55) finish()
  }
  function end(event: PointerEvent<HTMLCanvasElement>) {
    if (pointer.current !== event.pointerId) return
    pointer.current = null
    previous.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId)
  }
  return (
    <div>
      <div
        className={`${styles.ticket} ${imageOnly ? styles.imageOnlyTicket : ''}`}
        style={{ aspectRatio: `${cover.width}/${cover.height}` }}
        role={imageOnly && !revealed ? 'button' : undefined}
        tabIndex={imageOnly && !revealed ? 0 : undefined}
        aria-label={
          imageOnly && !revealed ? 'Revelar raspadinha pelo teclado' : undefined
        }
        onKeyDown={(event) => {
          if (imageOnly && !revealed && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault()
            finish()
          }
        }}
      >
        <img
          src={revealed ? result.url : cover.url}
          alt={revealed ? 'Resultado da sua raspadinha' : 'Capa da sua raspadinha'}
          draggable={false}
          onError={() => setFailed(true)}
        />
        {!revealed && (
          <>
            {ready && (
              <img
                className={styles.resultLayer}
                src={result.url}
                alt=""
                aria-hidden
                draggable={false}
                style={{
                  clipPath: `inset(${area.y}% ${100 - area.x - area.width}% ${100 - area.y - area.height}% ${area.x}%)`,
                }}
                onError={() => setFailed(true)}
              />
            )}
            <canvas
              ref={canvas}
              width={width}
              height={height}
              className={styles.canvas}
              style={{
                left: `${area.x}%`,
                top: `${area.y}%`,
                width: `${area.width}%`,
                height: `${area.height}%`,
              }}
              aria-hidden
              onPointerDown={(event) => {
                if (pointer.current !== null || event.button !== 0) return
                pointer.current = event.pointerId
                event.currentTarget.setPointerCapture(event.pointerId)
                scratch(event)
              }}
              onPointerMove={(event) => {
                if (pointer.current === event.pointerId) scratch(event)
              }}
              onPointerUp={end}
              onPointerCancel={end}
              onLostPointerCapture={end}
            />
            {!ready && !imageOnly && (
              <span className={styles.loadingCover}>Preparando sua raspadinha…</span>
            )}
          </>
        )}
      </div>
      {failed && (
        <p role="status">
          Não foi possível carregar a imagem. Você pode revelar seu resultado pelo botão
          abaixo.
        </p>
      )}
      {!revealed && (!imageOnly || failed) && (
        <div className={styles.scratchControls}>
          <div
            className={styles.progress}
            role="progressbar"
            aria-label="Área raspada"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
          >
            <span style={{ width: `${progress}%` }} />
          </div>
          <p className={styles.hint}>
            Passe o dedo ou o mouse na área raspável para descobrir.
          </p>
          <button type="button" className={styles.quietButton} onClick={finish}>
            Revelar sem raspar
          </button>
        </div>
      )}
    </div>
  )
}
